import React, {useEffect, useState} from 'react';
import './FinancialDashboard.css';
import AuthenticatedLayout from '../authenticatedLayout/AuthenticatedLayout';
import {FiCalendar, FiCheck, FiCreditCard, FiDollarSign, FiEdit, FiPlus, FiTrash2, FiX} from 'react-icons/fi';
import axios from 'axios';
import '../../App.css';
import {USER_ENDPOINTS} from '../../Constants';

const FinancialDashboard = () => {
    // State for the selected month and year
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState({
        incomes: false,
        expenses: false,
        employees: false,
        payrolls: false
    });
    const [error, setError] = useState({
        incomes: null,
        expenses: null,
        employees: null,
        payrolls: null
    });

    // State for financial data
    const [incomes, setIncomes] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [payrolls, setPayrolls] = useState([]);
    const [editingPayroll, setEditingPayroll] = useState(null);
    const [editingExpense, setEditingExpense] = useState(null);
    const [isAddingExpense, setIsAddingExpense] = useState(false);
    const [newExpense, setNewExpense] = useState({
        direction: 'OUTGOING',
        transactionType: 'CASH',
        amount: '',
        paymentDate: new Date().toISOString().split('T')[0],
        description: '',
        isPaid: true,
        paymentType: 'OPERATIONAL_EXPENSE'
    });

    // State for monthly data when "Toate" is selected
    const [monthlyData, setMonthlyData] = useState([]);

    // State for delete confirmation modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [expenseToDelete, setExpenseToDelete] = useState(null);

    // Animation duration in ms (should match CSS animation time)
    const animationDuration = 300;

    // Months in Romanian
    const months = [
        'Toate', 'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
        'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
    ];

    // Years for selection (current year and 4 years back)
    const currentYear = new Date().getFullYear();
    const years = [
        (currentYear - 2).toString(),
        (currentYear - 1).toString(),
        currentYear.toString(),
        (currentYear + 1).toString()
    ];

    // Payment type mapping for income table
    const getPaymentTypeLabel = (type) => {
        const paymentTypeMapping = {
            'DEPOSIT': 'Avans',
            'FINAL_PAYMENT': 'Rest de plată',
            'INSTALLATION_FEE': 'Taxă instalare',
            'DESIGN_FEE': 'Taxă design',
            'MATERIAL_COST': 'Cost materiale',
            'MODIFICATION_FEE': 'Taxă modificare',
            'DELIVERY_CHARGE': 'Taxă livrare',
            'WARRANTY_EXTENSION': 'Extindere garanție',
            'OPERATIONAL_EXPENSE': 'Cheltuieli operaționale',
            'SUPPLIER_PAYMENT': 'Plată furnizor',
            'EMPLOYEE_SALARY': 'Salariu angajat',
            'OTHER': 'Altele'
        };
        return paymentTypeMapping[type] || type;
    };

    // Expense types mapping
    const expenseTypeOptions = [
        {value: 'DEPOSIT', label: 'Avans'},
        {value: 'FINAL_PAYMENT', label: 'Lichidare'},
        {value: 'INSTALLATION_FEE', label: 'Taxă instalare'},
        {value: 'DESIGN_FEE', label: 'Taxă design'},
        {value: 'MATERIAL_COST', label: 'Cost materiale'},
        {value: 'MODIFICATION_FEE', label: 'Taxă modificare'},
        {value: 'DELIVERY_CHARGE', label: 'Taxă livrare'},
        {value: 'WARRANTY_EXTENSION', label: 'Extindere garanție'},
        {value: 'OPERATIONAL_EXPENSE', label: 'Cheltuieli operaționale'},
        {value: 'SUPPLIER_PAYMENT', label: 'Plată furnizor'},
        {value: 'EMPLOYEE_SALARY', label: 'Salariu angajat'},
        {value: 'OTHER', label: 'Altele'}
    ];

    // Transaction type options
    const transactionTypeOptions = [
        {value: 'CASH', label: 'Numerar'},
        {value: 'CARD', label: 'Card'}
    ];

    // Fetch data when month or year changes
    useEffect(() => {
        if (selectedMonth === 0) {
            fetchAllMonthsData();
        } else {
            fetchIncomes();
            fetchExpenses();
            fetchEmployees();
            fetchPayrolls();
        }
    }, [selectedMonth, selectedYear]);

    // Fetch data for all months when "Toate" is selected
    const fetchAllMonthsData = async () => {
        setLoading(prev => ({...prev, incomes: true, expenses: true, payrolls: true}));
        setError(prev => ({...prev, incomes: null, expenses: null, payrolls: null}));

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            // Make a single call with month=0 to get all data for the year
            const [incomesResponse, expensesResponse, payrollsResponse] = await Promise.all([
                axios.get(USER_ENDPOINTS.INBOUND, {
                    params: {
                        month: '0',
                        year: selectedYear.toString()
                    },
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }),
                axios.get(USER_ENDPOINTS.OUTGOING, {
                    params: {
                        month: '0',
                        year: selectedYear.toString()
                    },
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }),
                axios.get(USER_ENDPOINTS.PAYROLL, {
                    params: {
                        month: '0',
                        year: selectedYear.toString()
                    },
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                })
            ]);

            // Set the data for display
            setIncomes(incomesResponse.data);
            setExpenses(expensesResponse.data);
            setPayrolls(payrollsResponse.data);

            // Group the data by month for the monthly summary table
            const groupedData = [];

            // Initialize all months with zero values
            for (let month = 1; month <= 12; month++) {
                groupedData.push({
                    month,
                    totalIncomes: 0,
                    totalExpenses: 0,
                    totalSalaries: 0,
                    profit: 0
                });
            }

            // Process incomes
            incomesResponse.data.forEach(income => {
                const incomeDate = new Date(income.paymentDate);
                const incomeMonth = incomeDate.getMonth() + 1;
                const monthData = groupedData.find(data => data.month === incomeMonth);
                if (monthData) {
                    monthData.totalIncomes += parseFloat(income.amount);
                }
            });

            // Process expenses
            expensesResponse.data.forEach(expense => {
                const expenseDate = new Date(expense.paymentDate);
                const expenseMonth = expenseDate.getMonth() + 1;
                const monthData = groupedData.find(data => data.month === expenseMonth);
                if (monthData) {
                    monthData.totalExpenses += parseFloat(expense.amount);
                }
            });

            // Process payrolls
            payrollsResponse.data.forEach(payroll => {
                const payrollDate = new Date(payroll.payrollDate);
                const payrollMonth = payrollDate.getMonth() + 1;
                const monthData = groupedData.find(data => data.month === payrollMonth);
                if (monthData) {
                    monthData.totalSalaries += parseFloat(payroll.totalSalary || 0);
                }
            });

            // Calculate profit for each month
            groupedData.forEach(monthData => {
                monthData.profit = monthData.totalIncomes - (monthData.totalExpenses + monthData.totalSalaries);
            });

            setMonthlyData(groupedData);

        } catch (err) {
            console.error('Error fetching all months data:', err);
            setError(prev => ({
                ...prev,
                incomes: 'Nu s-au putut încărca datele. Vă rugăm încercați din nou.',
                expenses: 'Nu s-au putut încărca datele. Vă rugăm încercați din nou.',
                payrolls: 'Nu s-au putut încărca datele. Vă rugăm încercați din nou.'
            }));
        } finally {
            setLoading(prev => ({...prev, incomes: false, expenses: false, payrolls: false}));
        }
    };

    // Fetch income data from the API
    const fetchIncomes = async () => {
        setLoading(prev => ({...prev, incomes: true}));
        setError(prev => ({...prev, incomes: null}));

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.get(USER_ENDPOINTS.INBOUND, {
                params: {
                    month: selectedMonth.toString(),
                    year: selectedYear.toString()
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            setIncomes(response.data);
        } catch (err) {
            console.error('Error fetching income data:', err);
            setError(prev => ({
                ...prev,
                incomes: 'Nu s-au putut încărca datele privind încasările. Vă rugăm încercați din nou.'
            }));
        } finally {
            setLoading(prev => ({...prev, incomes: false}));
        }
    };

    // Fetch expense data from the API
    const fetchExpenses = async () => {
        setLoading(prev => ({...prev, expenses: true}));
        setError(prev => ({...prev, expenses: null}));

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.get(USER_ENDPOINTS.OUTGOING, {
                params: {
                    month: selectedMonth.toString(),
                    year: selectedYear.toString()
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            setExpenses(response.data);
        } catch (err) {
            console.error('Error fetching expense data:', err);
            setError(prev => ({
                ...prev,
                expenses: 'Nu s-au putut încărca datele privind cheltuielile. Vă rugăm încercați din nou.'
            }));
        } finally {
            setLoading(prev => ({...prev, expenses: false}));
        }
    };

    // Fetch employee data from the API
    const fetchEmployees = async () => {
        setLoading(prev => ({...prev, employees: true}));
        setError(prev => ({...prev, employees: null}));

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.get(USER_ENDPOINTS.EMPLOYEE, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            setEmployees(response.data);
        } catch (err) {
            console.error('Error fetching employee data:', err);
            setError(prev => ({
                ...prev,
                employees: 'Nu s-au putut încărca datele despre angajați. Vă rugăm încercați din nou.'
            }));
        } finally {
            setLoading(prev => ({...prev, employees: false}));
        }
    };

    // Fetch payroll data from the API
    const fetchPayrolls = async () => {
        setLoading(prev => ({...prev, payrolls: true}));
        setError(prev => ({...prev, payrolls: null}));

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            // If 'Toate' is selected, send 0 as month
            const monthToSend = selectedMonth === 0 ? '0' : selectedMonth.toString();

            const response = await axios.get(USER_ENDPOINTS.PAYROLL, {
                params: {
                    month: monthToSend,
                    year: selectedYear.toString()
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            setPayrolls(response.data);
        } catch (err) {
            console.error('Error fetching payroll data:', err);
            setError(prev => ({
                ...prev,
                payrolls: 'Nu s-au putut încărca datele despre salarii. Vă rugăm încercați din nou.'
            }));
        } finally {
            setLoading(prev => ({...prev, payrolls: false}));
        }
    };

    // Handle adding a new expense
    const handleAddExpense = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            await axios.post(USER_ENDPOINTS.PAYMENT_CREATE, newExpense, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            // Refresh expense data
            await fetchExpenses();

            // Reset form and close it
            setNewExpense({
                direction: 'OUTGOING',
                transactionType: 'CASH',
                amount: '',
                paymentDate: new Date().toISOString().split('T')[0],
                description: '',
                isPaid: true,
                paymentType: 'OPERATIONAL_EXPENSE'
            });
            setIsAddingExpense(false);
        } catch (err) {
            console.error('Error adding expense:', err);
            alert('A apărut o eroare la adăugarea cheltuielii. Vă rugăm încercați din nou.');
        }
    };

    // Handle editing an expense
    const handleEditExpense = (expense) => {
        setEditingExpense({
            ...expense,
            paymentDate: expense.paymentDate.split('T')[0], // Format date for input field
            amount: expense.amount.toString() // Convert to string for input field
        });
    };

    // Handle canceling expense edit
    const handleCancelEditExpense = () => {
        setEditingExpense(null);
    };

    // Handle updating expense
    const handleUpdateExpense = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            await axios.put(`${USER_ENDPOINTS.PAYMENT_UPDATE}/${editingExpense.id}`, {
                ...editingExpense,
                amount: parseFloat(editingExpense.amount)
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            // Refresh expense data
            await fetchExpenses();
            setEditingExpense(null);
        } catch (err) {
            console.error('Error updating expense:', err);
            alert('A apărut o eroare la actualizarea cheltuielii. Vă rugăm încercați din nou.');
        }
    };

    // Handle expense field change
    const handleExpenseFieldChange = (field, value) => {
        setEditingExpense(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Open delete confirmation modal
    const handleOpenDeleteModal = (expense) => {
        setExpenseToDelete(expense);
        setIsClosing(false);
        setShowDeleteModal(true);
    };

    // Close delete confirmation modal
    const handleCloseDeleteModal = () => {
        setIsClosing(true);
        setTimeout(() => {
            setShowDeleteModal(false);
            setExpenseToDelete(null);
            setIsClosing(false);
        }, animationDuration);
    };

    // Handle delete confirmation
    const confirmDelete = async () => {
        if (!expenseToDelete) return;

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            await axios.delete(`${USER_ENDPOINTS.PAYMENT}/${expenseToDelete.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            // Refresh expense data
            await fetchExpenses();
            handleCloseDeleteModal();
        } catch (err) {
            console.error('Error deleting expense:', err);
            alert('A apărut o eroare la ștergerea cheltuielii. Vă rugăm încercați din nou.');
            handleCloseDeleteModal();
        }
    };

    // Handle editing payroll
    const handleEditPayroll = (payroll) => {
        setEditingPayroll({
            ...payroll,
            vacationDays: payroll.vacationDays || 0,
            medicalLeaveDays: payroll.medicalLeaveDays || 0,
            unpaidLeaveDays: payroll.unpaidLeaveDays || 0,
            remainingVacationDays: payroll.employee?.remainingVacationDays || 0,
            extraHours: payroll.extraHours || 0,
            firstPayment: payroll.firstPayment || 0,
            totalSalary: payroll.totalSalary || 0
        });
    };

    // Handle canceling payroll edit
    const handleCancelEditPayroll = () => {
        setEditingPayroll(null);
    };

    // Handle updating payroll
    const handleUpdatePayroll = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            await axios.put(USER_ENDPOINTS.PAYROLL, {
                payrollId: editingPayroll.id,
                vacationDays: parseInt(editingPayroll.vacationDays) || 0,
                medicalLeaveDays: parseInt(editingPayroll.medicalLeaveDays) || 0,
                unpaidLeaveDays: parseInt(editingPayroll.unpaidLeaveDays) || 0,
                extraHours: parseInt(editingPayroll.extraHours) || 0,
                firstPayment: parseFloat(editingPayroll.firstPayment) || 0,
                totalSalary: parseFloat(editingPayroll.totalSalary) || 0
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            // Refresh payroll data
            await fetchPayrolls();
            setEditingPayroll(null);
        } catch (err) {
            console.error('Error updating payroll:', err);
            alert('A apărut o eroare la actualizarea salariului. Vă rugăm încercați din nou.');
        }
    };

    // Handle payroll field change
    const handlePayrollFieldChange = (field, value) => {
        // Convert the value to number if it's empty, set to 0
        let numericValue = value === '' ? 0 : value;

        // Parse to proper numeric type for numeric fields
        if (field !== 'description') {
            numericValue = parseInt(numericValue) || 0;
        }

        setEditingPayroll(prev => ({
            ...prev,
            [field]: numericValue
        }));
    };

    // Calculate totals for each section
    const calculateTotals = () => {
        const totalIncomes = incomes.reduce((sum, income) => sum + parseFloat(income.amount), 0);
        const totalExpenses = expenses.reduce((sum, expense) => sum + parseFloat(expense.amount), 0);
        const totalSalaries = payrolls.reduce((sum, payroll) => sum + parseFloat(payroll.totalSalary || 0), 0);

        const profit = totalIncomes - (totalExpenses + totalSalaries);

        return {
            totalIncomes,
            totalExpenses,
            totalSalaries,
            profit
        };
    };

    const {totalIncomes, totalExpenses, totalSalaries, profit} = calculateTotals();

    // Format currency for display
    const formatCurrency = (value) => {
        if (!value && value !== 0) return '';
        return new Intl.NumberFormat('ro-RO', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(value);
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('ro-RO');
    };

    // Get expense type label
    const getExpenseTypeLabel = (type) => {
        const found = expenseTypeOptions.find(option => option.value === type);
        return found ? found.label : type;
    };

    return (
        <AuthenticatedLayout>
            <div className="financial-dashboard">
                <div className="header-container">
                    <h1 className="page-title">Bugetare</h1>
                    <div className="dashboard-header">
                        <div className="filters">
                            <div className="dropdown">
                                <button className="dropdown-button">{months[selectedMonth]}</button>
                                <div className="dropdown-content">
                                    {months.map((month, index) => (
                                        <a key={month} onClick={() => setSelectedMonth(index)}>{month}</a>
                                    ))}
                                </div>
                            </div>

                            <div className="dropdown">
                                <button className="dropdown-button">{selectedYear}</button>
                                <div className="dropdown-content">
                                    {years.map(year => (
                                        <a key={year} onClick={() => setSelectedYear(parseInt(year))}>{year}</a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Incasari (Income) section */}
                <div className="section incasari-section animate-section">
                    <div className="section-header">
                        <h2>Încasări</h2>
                    </div>

                    {loading.incomes ? (
                        <div className="loading-message">Se încarcă datele...</div>
                    ) : error.incomes ? (
                        <div className="error-message">{error.incomes}</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="data-table">
                                <thead>
                                <tr>
                                    <th>DATA</th>
                                    <th>NUME CLIENT</th>
                                    <th>ADRESA CLIENT</th>
                                    <th>TIP MOBILIER</th>
                                    <th>TIP DE PLATA</th>
                                    <th>MODALITATE DE PLATA</th>
                                    <th>SUMĂ</th>
                                </tr>
                                </thead>
                                <tbody>
                                {incomes.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="no-data">Nu există încasări pentru perioada
                                            selectată
                                        </td>
                                    </tr>
                                ) : (
                                    incomes.map(income => (
                                        <tr key={income.id} className="data-row">
                                            <td>
                                                <div className="date-cell">
                                                    {formatDate(income.paymentDate)} <FiCalendar
                                                    className="icon-small"/>
                                                </div>
                                            </td>
                                            <td>{income.customer?.firstName || 'N/A'} {income.customer?.lastName || 'N/A'}</td>
                                            <td>{income.customer?.homeAddress || 'N/A'}</td>
                                            <td>{income.customer?.furnitureType || 'N/A'}</td>
                                            <td className="payment-type-cell">
                                                {getPaymentTypeLabel(income.paymentType)}
                                            </td>
                                            <td className="method-cell">
                                                {income.transactionType === 'CASH' ? (
                                                    <span><FiDollarSign className="icon-small"/> Numerar</span>
                                                ) : (
                                                    <span><FiCreditCard className="icon-small"/> Card</span>
                                                )}
                                            </td>
                                            <td className="amount-cell">{formatCurrency(income.amount)} lei</td>
                                        </tr>
                                    ))
                                )}
                                </tbody>
                                <tfoot>
                                <tr>
                                    <td colSpan="5" className="total-text">TOTAL ÎNCASĂRI:</td>
                                    <td colSpan="2" className="total-amount">{formatCurrency(totalIncomes)} lei</td>
                                </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>

                {/* Cheltuieli (Expenses) section */}
                <div className="section cheltuieli-section animate-section">
                    <div className="section-header">
                        <h2>Cheltuieli</h2>
                        <button className="add-button" onClick={() => setIsAddingExpense(true)}>
                            <FiPlus/> Adaugă cheltuială
                        </button>
                    </div>

                    {loading.expenses ? (
                        <div className="loading-message">Se încarcă datele...</div>
                    ) : error.expenses ? (
                        <div className="error-message">{error.expenses}</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="data-table">
                                <thead>
                                <tr>
                                    <th>DATA</th>
                                    <th>TIP</th>
                                    <th>NUME ACHIZITIE</th>
                                    <th>METODĂ</th>
                                    <th>SUMĂ</th>
                                    <th>ACȚIUNI</th>
                                </tr>
                                </thead>
                                <tbody>
                                {isAddingExpense && (
                                    <tr className="new-expense-row">
                                        <td>
                                            <input
                                                type="date"
                                                value={newExpense.paymentDate}
                                                onChange={(e) => setNewExpense({
                                                    ...newExpense,
                                                    paymentDate: e.target.value
                                                })}
                                                className="form-input"
                                            />
                                        </td>
                                        <td>
                                            <select
                                                value={newExpense.paymentType}
                                                onChange={(e) => setNewExpense({
                                                    ...newExpense,
                                                    paymentType: e.target.value
                                                })}
                                                className="form-input"
                                            >
                                                {expenseTypeOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                value={newExpense.description}
                                                onChange={(e) => setNewExpense({
                                                    ...newExpense,
                                                    description: e.target.value
                                                })}
                                                placeholder="Descriere"
                                                className="form-input"
                                            />
                                        </td>
                                        <td>
                                            <select
                                                value={newExpense.transactionType}
                                                onChange={(e) => setNewExpense({
                                                    ...newExpense,
                                                    transactionType: e.target.value
                                                })}
                                                className="form-input"
                                            >
                                                {transactionTypeOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                value={newExpense.amount}
                                                onChange={(e) => setNewExpense({...newExpense, amount: e.target.value})}
                                                placeholder="0"
                                                className="form-input"
                                            />
                                        </td>
                                        <td className="actions">
                                            <button
                                                className="action-button approve-btn"
                                                onClick={handleAddExpense}
                                                title="Salvează"
                                            >
                                                <FiCheck/>
                                            </button>
                                            <button
                                                className="action-button reject-btn"
                                                onClick={() => setIsAddingExpense(false)}
                                                title="Anulează"
                                            >
                                                <FiX/>
                                            </button>
                                        </td>
                                    </tr>
                                )}

                                {expenses.length === 0 && !isAddingExpense ? (
                                    <tr>
                                        <td colSpan="6" className="no-data">Nu există cheltuieli pentru perioada
                                            selectată
                                        </td>
                                    </tr>
                                ) : (
                                    expenses.map(expense => (
                                        <tr key={expense.id} className="data-row">
                                            {editingExpense && editingExpense.id === expense.id ? (
                                                <>
                                                    <td>
                                                        <input
                                                            type="date"
                                                            value={editingExpense.paymentDate}
                                                            onChange={(e) => handleExpenseFieldChange('paymentDate', e.target.value)}
                                                            className="form-input"
                                                        />
                                                    </td>
                                                    <td>
                                                        <select
                                                            value={editingExpense.paymentType}
                                                            onChange={(e) => handleExpenseFieldChange('paymentType', e.target.value)}
                                                            className="form-input"
                                                        >
                                                            {expenseTypeOptions.map(option => (
                                                                <option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            value={editingExpense.description}
                                                            onChange={(e) => handleExpenseFieldChange('description', e.target.value)}
                                                            className="form-input"
                                                        />
                                                    </td>
                                                    <td>
                                                        <select
                                                            value={editingExpense.transactionType}
                                                            onChange={(e) => handleExpenseFieldChange('transactionType', e.target.value)}
                                                            className="form-input"
                                                        >
                                                            {transactionTypeOptions.map(option => (
                                                                <option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={editingExpense.amount}
                                                            onChange={(e) => handleExpenseFieldChange('amount', e.target.value)}
                                                            className="form-input"
                                                        />
                                                    </td>
                                                    <td className="actions">
                                                        <button
                                                            className="action-button approve-btn"
                                                            onClick={handleUpdateExpense}
                                                            title="Salvează"
                                                        >
                                                            <FiCheck/>
                                                        </button>
                                                        <button
                                                            className="action-button reject-btn"
                                                            onClick={handleCancelEditExpense}
                                                            title="Anulează"
                                                        >
                                                            <FiX/>
                                                        </button>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td>
                                                        <div className="date-cell">
                                                            {formatDate(expense.paymentDate)} <FiCalendar
                                                            className="icon-small"/>
                                                        </div>
                                                    </td>
                                                    <td>{getExpenseTypeLabel(expense.paymentType)}</td>
                                                    <td>{expense.description}</td>
                                                    <td className="method-cell">
                                                        {expense.transactionType === 'CASH' ? (
                                                            <span><FiDollarSign className="icon-small"/> Numerar</span>
                                                        ) : (
                                                            <span><FiCreditCard className="icon-small"/> Card</span>
                                                        )}
                                                    </td>
                                                    <td className="amount-cell">{formatCurrency(expense.amount)} lei</td>
                                                    <td className="actions">
                                                        <button
                                                            className="action-button edit-button"
                                                            onClick={() => handleEditExpense(expense)}
                                                            title="Editează"
                                                        >
                                                            <FiEdit/>
                                                        </button>
                                                        <button
                                                            className="action-button delete-button"
                                                            onClick={() => handleOpenDeleteModal(expense)}
                                                            title="Șterge"
                                                        >
                                                            <FiTrash2/>
                                                        </button>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))
                                )}
                                </tbody>
                                <tfoot>
                                <tr>
                                    <td colSpan="4" className="total-text">TOTAL CHELTUIELI:</td>
                                    <td colSpan="2" className="total-amount">{formatCurrency(totalExpenses)} lei</td>
                                </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>

                {/* Salarii (Salaries) section */}
                <div className="section salarii-section animate-section">
                    <div className="section-header">
                        <h2>Salarii</h2>
                    </div>

                    {loading.payrolls ? (
                        <div className="loading-message">Se încarcă datele...</div>
                    ) : error.payrolls ? (
                        <div className="error-message">{error.payrolls}</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="data-table">
                                <thead>
                                <tr>
                                    <th>SALARIAT</th>
                                    <th>CONCEDIU ODIHNĂ</th>
                                    <th>CONCEDIU MEDICAL</th>
                                    <th>CONCEDIU FĂRĂ PLATĂ</th>
                                    <th>CONCEDIU RĂMAS</th>
                                    <th>ORE EXTRA</th>
                                    <th>AVANS</th>
                                    <th>SALARIU</th>
                                    <th>ACȚIUNI</th>
                                </tr>
                                </thead>
                                <tbody>
                                {payrolls.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="no-data">Nu există date despre salarii pentru
                                            perioada selectată
                                        </td>
                                    </tr>
                                ) : (
                                    payrolls.map(payroll => (
                                        <tr key={payroll.id} className="data-row">
                                            {editingPayroll && editingPayroll.id === payroll.id ? (
                                                <>
                                                    <td>{payroll.employee.name}</td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={editingPayroll.vacationDays}
                                                            onChange={(e) => handlePayrollFieldChange('vacationDays', e.target.value)}
                                                            className="form-input"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={editingPayroll.medicalLeaveDays}
                                                            onChange={(e) => handlePayrollFieldChange('medicalLeaveDays', e.target.value)}
                                                            className="form-input"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={editingPayroll.unpaidLeaveDays}
                                                            onChange={(e) => handlePayrollFieldChange('unpaidLeaveDays', e.target.value)}
                                                            className="form-input"
                                                        />
                                                    </td>
                                                    <td>{payroll.employee.remainingVacationDays}</td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={editingPayroll.extraHours}
                                                            onChange={(e) => handlePayrollFieldChange('extraHours', e.target.value)}
                                                            className="form-input"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={editingPayroll.firstPayment}
                                                            onChange={(e) => handlePayrollFieldChange('firstPayment', e.target.value)}
                                                            className="form-input"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={editingPayroll.totalSalary}
                                                            className="form-input"
                                                            readOnly
                                                            disabled
                                                        />
                                                    </td>
                                                    <td className="actions">
                                                        <button
                                                            className="action-button approve-btn"
                                                            onClick={handleUpdatePayroll}
                                                            title="Salvează"
                                                        >
                                                            <FiCheck/>
                                                        </button>
                                                        <button
                                                            className="action-button reject-btn"
                                                            onClick={handleCancelEditPayroll}
                                                            title="Anulează"
                                                        >
                                                            <FiX/>
                                                        </button>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td>{payroll.employee.name}</td>
                                                    <td>{payroll.vacationDays} zile</td>
                                                    <td>{payroll.medicalLeaveDays} zile</td>
                                                    <td>{payroll.unpaidLeaveDays} zile</td>
                                                    <td>{payroll.employee.remainingVacationDays} zile</td>
                                                    <td>{payroll.extraHours} ore</td>
                                                    <td className="amount-cell">{formatCurrency(payroll.firstPayment)} lei</td>
                                                    <td className="amount-cell">{formatCurrency(payroll.totalSalary)} lei</td>
                                                    <td className="actions">
                                                        <button
                                                            className="action-button edit-button"
                                                            onClick={() => handleEditPayroll(payroll)}
                                                            title="Editează"
                                                        >
                                                            <FiEdit/>
                                                        </button>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))
                                )}
                                </tbody>
                                <tfoot>
                                <tr>
                                    <td colSpan="7" className="total-text">TOTAL SALARII:</td>
                                    <td colSpan="2" className="total-amount">
                                        {formatCurrency(payrolls.reduce((sum, payroll) => sum + parseFloat(payroll.totalSalary), 0))} lei
                                    </td>
                                </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>

                {/* Profit/media section */}
                <div className="section profit-section animate-section">
                    <div className="section-header">
                        <h2>Profit/media</h2>
                    </div>

                    <div className="table-responsive">
                        <table className="data-table profit-table">
                            <thead>
                            <tr>
                                <th>LUNA</th>
                                <th>ÎNCASĂRI</th>
                                <th>CHELTUIELI</th>
                                <th>SALARII</th>
                                <th>PROFIT</th>
                            </tr>
                            </thead>
                            <tbody>
                            {selectedMonth === 0 ? (
                                monthlyData.map(data => (
                                    <tr key={data.month} className="data-row">
                                        <td>{months[data.month]} {selectedYear}</td>
                                        <td className="amount-cell income-amount">{formatCurrency(data.totalIncomes)} lei</td>
                                        <td className="amount-cell expense-amount">{formatCurrency(data.totalExpenses)} lei</td>
                                        <td className="amount-cell salary-amount">{formatCurrency(data.totalSalaries)} lei</td>
                                        <td className={`amount-cell profit-amount ${data.profit >= 0 ? 'positive-profit' : 'negative-profit'}`}>
                                            {formatCurrency(data.profit)} lei
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr className="data-row highlight-row">
                                    <td>{months[selectedMonth]} {selectedYear}</td>
                                    <td className="amount-cell income-amount">{formatCurrency(totalIncomes)} lei</td>
                                    <td className="amount-cell expense-amount">{formatCurrency(totalExpenses)} lei</td>
                                    <td className="amount-cell salary-amount">{formatCurrency(totalSalaries)} lei</td>
                                    <td className={`amount-cell profit-amount ${profit >= 0 ? 'positive-profit' : 'negative-profit'}`}>
                                        {formatCurrency(profit)} lei
                                    </td>
                                </tr>
                            )}
                            </tbody>
                            {selectedMonth === 0 && (
                                <tfoot>
                                <tr className="highlight-row">
                                    <td className="total-text">TOTAL {selectedYear}</td>
                                    <td className="amount-cell income-amount">
                                        {formatCurrency(monthlyData.reduce((sum, data) => sum + data.totalIncomes, 0))} lei
                                    </td>
                                    <td className="amount-cell expense-amount">
                                        {formatCurrency(monthlyData.reduce((sum, data) => sum + data.totalExpenses, 0))} lei
                                    </td>
                                    <td className="amount-cell salary-amount">
                                        {formatCurrency(monthlyData.reduce((sum, data) => sum + data.totalSalaries, 0))} lei
                                    </td>
                                    <td className={`amount-cell profit-amount ${monthlyData.reduce((sum, data) => sum + data.profit, 0) >= 0 ? 'positive-profit' : 'negative-profit'}`}>
                                        {formatCurrency(monthlyData.reduce((sum, data) => sum + data.profit, 0))} lei
                                    </td>
                                </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>

                {/* Delete Confirmation Modal */}
                {showDeleteModal && expenseToDelete && (
                    <div className={`modal-overlay ${isClosing ? 'closing' : ''}`}>
                        <div className="modal-content delete-modal">
                            <div className="modal-header delete-modal-header">
                                <h2 className="modal-title">Confirmă ștergerea</h2>
                                <button className="close-button" onClick={handleCloseDeleteModal}>
                                    <FiX/>
                                </button>
                            </div>

                            <div className="modal-body delete-modal-body">
                                <div className="delete-warning">
                                    <p className="delete-message">
                                        Sunteți sigur că doriți să ștergeți cheltuiala:
                                        <strong> {expenseToDelete.description}</strong>?
                                    </p>
                                    <p className="delete-warning-text">
                                        Această acțiune nu poate fi anulată.
                                    </p>
                                </div>

                                <div className="modal-footer delete-modal-footer">
                                    <button
                                        type="button"
                                        className="save-button"
                                        onClick={handleCloseDeleteModal}
                                    >
                                        <FiX/> Nu
                                    </button>
                                    <button
                                        type="button"
                                        className="cancel-button"
                                        onClick={confirmDelete}
                                    >
                                        <FiTrash2/> Da
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AuthenticatedLayout>
    );
};

export default FinancialDashboard;